// Pure helpers for proof photos (no browser or network), so they can be unit-tested.

export const MAX_BYTES = 2 * 1024 * 1024 // the database and the storage bucket both refuse more
export const MAX_EDGE = 1600 // longest side of the stored photo, in pixels
export const THUMB_EDGE = 320
export const MAX_ATTEMPTS = 8

export type ProofMime = 'image/webp' | 'image/jpeg'

export function extFor(mime: ProofMime): 'webp' | 'jpg' {
  return mime === 'image/webp' ? 'webp' : 'jpg'
}

/** Size to scale (w x h) down to so the longest side is at most `max`; never scales up. */
export function scaledSize(w: number, h: number, max: number): { width: number; height: number } {
  if (w <= 0 || h <= 0) return { width: 1, height: 1 }
  const longest = Math.max(w, h)
  if (longest <= max) return { width: w, height: h }
  const k = max / longest
  return { width: Math.max(1, Math.round(w * k)), height: Math.max(1, Math.round(h * k)) }
}

const pad = (n: number) => String(n).padStart(2, '0')

/**
 * Where a department's proof file lives. The department id is the SECOND segment: the storage
 * policy (0012) relies on that. `date` is a business date 'YYYY-MM-DD' (never a Date).
 */
export function storagePaths(a: {
  departmentId: string
  subject: 'expenses' | 'payments' | 'remittances' | 'sheets'
  date: string
  id: string
  mime: ProofMime
}): { file: string; thumb: string } {
  const m = /^(\d{4})-(\d{2})-\d{2}$/.exec(a.date)
  if (!m) throw new Error(`Invalid date '${a.date}'`)
  const dir = `d/${a.departmentId}/${a.subject}/${m[1]}/${pad(Number(m[2]))}`
  return { file: `${dir}/${a.id}.${extFor(a.mime)}`, thumb: `${dir}/${a.id}_t.webp` }
}

/**
 * Where a CENTRAL proof file lives (the church treasury's own count sheets and transfer slips, which
 * belong to no department). Under central/, which only the church treasurer can write and only
 * church-wide readers can read (storage policy 0012). The database checks this prefix per kind (0017).
 */
export function centralStoragePaths(a: {
  subject: 'income' | 'transfer'
  date: string
  id: string
  mime: ProofMime
}): { file: string; thumb: string } {
  const m = /^(\d{4})-(\d{2})-\d{2}$/.exec(a.date)
  if (!m) throw new Error(`Invalid date '${a.date}'`)
  const dir = `central/${a.subject}/${m[1]}/${pad(Number(m[2]))}`
  return { file: `${dir}/${a.id}.${extFor(a.mime)}`, thumb: `${dir}/${a.id}_t.webp` }
}

/** Seconds to wait before the next try: 5s, 15s, 45s, 2m, 5m, 10m, 20m, 30m (capped). */
export function retryDelayMs(attempts: number): number {
  const steps = [5, 15, 45, 120, 300, 600, 1200, 1800]
  return steps[Math.min(Math.max(attempts - 1, 0), steps.length - 1)] * 1000
}

export type QueueStatus = 'queued' | 'failed' | 'done'

/** What to tell the treasurer about a photo that has not finished uploading. */
export function describeQueueEntry(e: { status: QueueStatus; attempts: number; lastError?: string | null }): string {
  if (e.status === 'failed') return 'Could not upload this photo. It is saved on this device.'
  if (e.attempts === 0) return 'Waiting to upload'
  return 'Waiting for a connection, will keep trying'
}

/**
 * Errors where retrying cannot help: the server looked at the request and refused it (not allowed,
 * wrong shape, a rule in the database). Network trouble, timeouts, server hiccups and an expired
 * sign-in are NOT permanent.
 */
export function isPermanentFailure(err: { status?: number; code?: string; message?: string } | null | undefined): boolean {
  if (!err) return false
  const s = err.status ?? 0
  if (s === 401 || s === 429) return false // sign-in expired / slow down: try again later
  if (s === 400 || s === 403 || s === 404 || s === 413 || s === 415 || s === 422) return true
  const c = err.code ?? ''
  // Postgres refusals: our own rules (P0001), constraint failures (23xxx), no permission (42xxx),
  // and PostgREST's own request errors (PGRST1xx). None of these change by trying again.
  return c === 'P0001' || /^23\d{3}$/.test(c) || /^42\d{3}$/.test(c) || /^PGRST1/.test(c)
}

/** The request never got an answer (offline, dropped connection, timeout): worth retrying forever. */
export function isNetworkFailure(err: { status?: number; code?: string; message?: string } | null | undefined): boolean {
  if (!err) return false
  return !err.status && !err.code
}
